import * as React from 'react';

import { Button, Menu, Tooltip } from '@rematter/ui-web';
import {
  Undo2,
  Redo2,
  Copy,
  Clipboard,
  Scissors,
  LayoutGrid,
  ArrowDown,
  ArrowRight,
  Loader2,
} from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import type { LayoutDirection } from './layout/elkLayout';

type FlowEditorToolbarProps = {
  canUndo: boolean;
  canRedo: boolean;
  hasSelection: boolean;
  hasClipboard: boolean;
  isLayouting: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onCut: () => void;
  onLayout: (direction: LayoutDirection) => void;
};

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
const modKey = isMac ? '⌘' : 'Ctrl';

export const FlowEditorToolbar: React.FC<FlowEditorToolbarProps> = ({
  canUndo,
  canRedo,
  hasSelection,
  hasClipboard,
  isLayouting,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onCut,
  onLayout,
}) => {
  const t = useTranslations();

  return (
    <div className="@FlowEditorToolbar absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
      <div className="flex items-center gap-1 rounded-lg border border-neutral/20 bg-white px-2 py-1.5 shadow-lg">
        <Tooltip
          tip={`${t.editor.actions.undo} (${modKey}+Z)`}
          wrapWithButton={false}
        >
          <Button
            variant="outline"
            color="neutral"
            size="small"
            icon={<Undo2 className="size-4" />}
            isDisabled={!canUndo}
            onPress={onUndo}
            aria-label={t.editor.actions.undo}
          />
        </Tooltip>

        <Tooltip
          tip={`${t.editor.actions.redo} (${modKey}+Shift+Z)`}
          wrapWithButton={false}
        >
          <Button
            variant="outline"
            color="neutral"
            size="small"
            icon={<Redo2 className="size-4" />}
            isDisabled={!canRedo}
            onPress={onRedo}
            aria-label={t.editor.actions.redo}
          />
        </Tooltip>

        <div className="mx-1 h-6 w-px bg-neutral/20" />

        <Tooltip
          tip={`${t.editor.actions.cut} (${modKey}+X)`}
          wrapWithButton={false}
        >
          <Button
            variant="outline"
            color="neutral"
            size="small"
            icon={<Scissors className="size-4" />}
            isDisabled={!hasSelection}
            onPress={onCut}
            aria-label={t.editor.actions.cut}
          />
        </Tooltip>

        <Tooltip
          tip={`${t.editor.actions.copy} (${modKey}+C)`}
          wrapWithButton={false}
        >
          <Button
            variant="outline"
            color="neutral"
            size="small"
            icon={<Copy className="size-4" />}
            isDisabled={!hasSelection}
            onPress={onCopy}
            aria-label={t.editor.actions.copy}
          />
        </Tooltip>

        <Tooltip
          tip={`${t.editor.actions.paste} (${modKey}+V)`}
          wrapWithButton={false}
        >
          <Button
            variant="outline"
            color="neutral"
            size="small"
            icon={<Clipboard className="size-4" />}
            isDisabled={!hasClipboard}
            onPress={onPaste}
            aria-label={t.editor.actions.paste}
          />
        </Tooltip>

        <div className="mx-1 h-6 w-px bg-neutral/20" />

        <Menu
          trigger={
            <Button
              variant="subtle"
              color="primary"
              size="small"
              icon={
                isLayouting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <LayoutGrid className="size-4" />
                )
              }
              isDisabled={isLayouting}
              aria-label={t.editor.actions.autoLayout}
            >
              {t.editor.actions.layout}
            </Button>
          }
          placement="top left"
          items={[
            {
              label: t.editor.actions.vertical,
              icon: <ArrowDown />,
              onAction: () => onLayout('DOWN'),
            },
            {
              label: t.editor.actions.horizontal,
              icon: <ArrowRight />,
              onAction: () => onLayout('RIGHT'),
            },
          ]}
        />
      </div>
    </div>
  );
};
